import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { ClubShieldConfig } from '../../../core/models/types';
import { clubsApi } from '../../../core/http/clubs.api';
import {
  ShieldTemplate,
  ShieldTemplateRegistry,
} from '../../../core/services/shield-template-registry.service';
import { ClubShieldComponent } from './club-shield.component';
import { IconComponent } from '../icon/icon.component';
import { GameService } from '../../../core/state/game.service';
import { confirmedClub, selectedClubId } from '../../../core/state/club-session';

/**
 * Validador de código hexadecimal de color (#RGB o #RRGGBB)
 */
function hexColorValidator(control: AbstractControl): ValidationErrors | null {
  if (!control.value) return null;
  const valid = /^#[0-9A-Fa-f]{6}$/.test(control.value.trim());
  return valid ? null : { invalidHex: true };
}

/**
 * Validador de hasta 3 letras o números opcionales
 */
function optionalInitialsValidator(control: AbstractControl): ValidationErrors | null {
  if (!control.value) return null;
  const valid = /^[A-Za-z0-9]{0,3}$/.test(control.value.trim());
  return valid ? null : { invalidInitials: true };
}

export interface PresetPalette {
  name: string;
  primary: string;
  secondary: string;
}

const PRESET_PALETTES: PresetPalette[] = [
  { name: 'Clásico Madrid', primary: '#123D83', secondary: '#F5B800' },
  { name: 'Azulgrana', primary: '#004D98', secondary: '#A50044' },
  { name: 'Rojiblanco', primary: '#CB3524', secondary: '#1B3F73' },
  { name: 'Verdiblanco', primary: '#0BB364', secondary: '#F5B800' },
  { name: 'Txuri-Urdin', primary: '#0067B1', secondary: '#FFFFFF' },
  { name: 'Naranja Ché', primary: '#EE7500', secondary: '#0F172A' },
  { name: 'Submarino', primary: '#005CA9', secondary: '#FDE100' },
  { name: 'Esmeralda Pro', primary: '#059669', secondary: '#10B981' },
];

@Component({
  selector: 'app-club-shield-editor',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ClubShieldComponent, IconComponent],
  templateUrl: './club-shield-editor.component.html',
  styleUrl: './club-shield-editor.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:keydown.escape)': 'onEscape()',
  },
})
export class ClubShieldEditorComponent {
  private readonly registry = inject(ShieldTemplateRegistry);
  private readonly game = inject(GameService);

  readonly clubId = input.required<string>();
  readonly clubName = input<string>('');
  readonly clubCode = input<string>('');
  readonly isOpen = input<boolean>(true);
  readonly canAdminister = input<boolean | null>(null);

  readonly saved = output<ClubShieldConfig>();
  readonly closed = output<void>();

  readonly loading = signal<boolean>(true);
  readonly saving = signal<boolean>(false);
  readonly error = signal<string | null>(null);
  readonly success = signal<boolean>(false);
  readonly serverNotice = signal<string | null>(null);

  readonly presets = PRESET_PALETTES;

  readonly allTemplates = computed(() => this.registry.getAllTemplates());

  readonly form = new FormGroup({
    templateId: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    primaryColor: new FormControl<string>('#123D83', {
      nonNullable: true,
      validators: [Validators.required, hexColorValidator],
    }),
    secondaryColor: new FormControl<string>('#F5B800', {
      nonNullable: true,
      validators: [Validators.required, hexColorValidator],
    }),
    textColor: new FormControl<string>('#FFFFFF', {
      nonNullable: true,
      validators: [Validators.required, hexColorValidator],
    }),
    initials: new FormControl<string>('LOM', {
      nonNullable: true,
      validators: [optionalInitialsValidator],
    }),
  });

  // Live real-time preview signals
  readonly currentTemplateId = signal<string>('');
  readonly currentPrimaryColor = signal<string>('#123D83');
  readonly currentSecondaryColor = signal<string>('#F5B800');
  readonly currentTextColor = signal<string>('#FFFFFF');
  readonly currentInitials = signal<string>('LOM');

  readonly previewShield = computed<ClubShieldConfig>(() => ({
    templateId: this.currentTemplateId(),
    primaryColor: this.currentPrimaryColor(),
    secondaryColor: this.currentSecondaryColor(),
    textColor: this.currentTextColor(),
    initials: this.currentInitials(),
  }));

  /**
   * Verificar autorización de administración del club
   */
  readonly isAuthorized = computed<boolean>(() => {
    if (this.canAdminister() !== null) {
      return !!this.canAdminister();
    }
    const targetId = this.clubId();
    if (!targetId) return false;

    // Usuario gestiona su propio club activo
    if (targetId === this.game.activeUserClubId()) return true;
    if (targetId === this.game.userClub()?.id) return true;
    if (targetId === selectedClubId()) return true;

    // Verificar si es propietario en sesión confirmada
    const confirmed = confirmedClub();
    if (confirmed && confirmed.clubId === targetId) {
      const user = this.game.currentUser();
      if (user && confirmed.ownerUserId && confirmed.ownerUserId === user.id) {
        return true;
      }
      return true;
    }

    return false;
  });

  constructor() {


    // Sincronizar valores del formulario con señales para preview en tiempo real
    this.form.valueChanges.subscribe((vals) => {
      if (vals.templateId !== undefined) this.currentTemplateId.set(vals.templateId || '');
      if (vals.primaryColor && /^#[0-9A-Fa-f]{6}$/.test(vals.primaryColor.trim())) {
        this.currentPrimaryColor.set(vals.primaryColor.trim().toUpperCase());
      }
      if (vals.secondaryColor && /^#[0-9A-Fa-f]{6}$/.test(vals.secondaryColor.trim())) {
        this.currentSecondaryColor.set(vals.secondaryColor.trim().toUpperCase());
      }
      if (vals.textColor && /^#[0-9A-Fa-f]{6}$/.test(vals.textColor.trim())) {
        this.currentTextColor.set(vals.textColor.trim().toUpperCase());
      }
      if (vals.initials !== undefined) {
        const clean = (vals.initials || '').replace(/[^A-Za-z0-9]/g, '').slice(0, 3).toUpperCase();
        this.currentInitials.set(clean);
      }
    });

    effect(() => {
      if (this.isOpen() && this.clubId()) {
        this.loadExistingConfig();
      }
    });
  }


  /**
   * Cargar configuración existente mediante GET /api/clubs/{clubId}/shield
   */
  async loadExistingConfig(): Promise<void> {
    const id = this.clubId();
    if (!id) return;

    this.loading.set(true);
    this.error.set(null);
    this.success.set(false);
    this.serverNotice.set(null);

    // Calcular valores por defecto basados en los datos del club si están disponibles
    const defaultInitials = (this.clubCode() || this.clubName() || 'LOM').replace(/[^A-Za-z0-9]/g, '').slice(0, 3).toUpperCase() || 'LOM';
    const fallbackPrimary = this.game.userClub()?.primaryColor || '#123D83';
    const fallbackSecondary = this.game.userClub()?.secondaryColor || '#F5B800';

    await this.registry.refreshTemplatesFromAssets();
    const defaultTemplateId = this.allTemplates()[0]?.id || '';

    try {
      const savedConfig = await clubsApi.getClubShield(id);

      if (savedConfig && savedConfig.templateId) {
        this.form.patchValue({
          templateId: this.registry.getTemplate(savedConfig.templateId) ? savedConfig.templateId : '',
          primaryColor: savedConfig.primaryColor || fallbackPrimary,
          secondaryColor: savedConfig.secondaryColor || fallbackSecondary,
          textColor: savedConfig.textColor || savedConfig.primaryColor || fallbackPrimary,
          initials: (savedConfig.initials ?? '').toUpperCase(),
        });
      } else {
        // Usar la primera plantilla existente cuando el club no tiene configuración
        this.form.patchValue({
          templateId: defaultTemplateId,
          primaryColor: fallbackPrimary,
          secondaryColor: fallbackSecondary,
          textColor: '#FFFFFF',
          initials: defaultInitials,
        });
      }
    } catch (e: any) {
      console.warn('[ClubShieldEditor] Error al cargar configuración de escudo:', e);
      // Inicializar con valores predeterminados seguros
      this.form.patchValue({
        templateId: defaultTemplateId,
        primaryColor: fallbackPrimary,
        secondaryColor: fallbackSecondary,
        textColor: '#FFFFFF',
        initials: defaultInitials,
      });
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * Manejar cambios en el input de iniciales: convertir a mayúsculas y filtrar caracteres no alfanuméricos
   */
  onInitialsInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const clean = input.value.replace(/[^A-Za-z0-9]/g, '').slice(0, 3).toUpperCase();
    this.form.controls.initials.setValue(clean, { emitEvent: true });
    input.value = clean;
  }

  /**
   * Aplicar una paleta preestablecida
   */
  applyPreset(preset: PresetPalette): void {
    this.form.patchValue({
      primaryColor: preset.primary,
      secondaryColor: preset.secondary,
    });
  }

  /**
   * Seleccionar plantilla
   */
  selectTemplate(template: ShieldTemplate): void {
    if (!template.available) return;
    this.form.controls.templateId.setValue(template.id);
  }

  /**
   * Guardar configuración mediante PUT /api/clubs/{clubId}/shield
   */
  async saveConfig(): Promise<void> {
    if (!this.isAuthorized()) {
      this.error.set('No tienes permisos de administración para personalizar el escudo de este club.');
      return;
    }

    if (!this.registry.getTemplate(this.form.controls.templateId.value)) {
      this.error.set('Selecciona una plantilla disponible.');
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.error.set('Por favor, revisa los datos: colores hexadecimales válidos y hasta 3 letras o números para las siglas (opcionales).');
      return;
    }

    const id = this.clubId();
    if (!id) {
      this.error.set('Identificador de club no disponible.');
      return;
    }

    const payload: ClubShieldConfig = {
      templateId: this.form.controls.templateId.value,
      primaryColor: this.form.controls.primaryColor.value.trim().toUpperCase(),
      secondaryColor: this.form.controls.secondaryColor.value.trim().toUpperCase(),
      textColor: this.form.controls.textColor.value.trim().toUpperCase(),
      initials: this.form.controls.initials.value.trim().toUpperCase(),
    };

    this.saving.set(true);
    this.error.set(null);
    this.success.set(false);

    try {
      const response = await clubsApi.updateClubShield(id, payload);

      // Confirmar persistencia
      this.success.set(true);
      this.serverNotice.set('¡Escudo personalizado guardado correctamente!');

      // Publish only the configuration confirmed by the server.
      this.saved.emit(response);

    } catch (err: any) {
      console.error('[ClubShieldEditor] Error guardando escudo:', err);

      let msg = 'No se ha podido guardar la personalización del escudo. Inténtalo de nuevo.';
      if (err.status === 403 || err.status === 401) {
        msg = 'No dispones de autorización para modificar el escudo de esta entidad.';
      } else if (err.status === 400) {
        msg = err.message || 'Los datos de la plantilla o colores no son válidos.';
      } else if (err.message) {
        msg = err.message;
      }
      this.error.set(msg);
    } finally {
      this.saving.set(false);
    }
  }

  close(): void {
    this.closed.emit();
  }

  onEscape(): void {
    if (this.isOpen() && !this.saving()) {
      this.close();
    }
  }
}
